import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  format, addDays, startOfWeek, isSameDay, parseISO, startOfMonth, addMonths,
} from "date-fns";
import {
  CalendarDays, ChevronLeft, ChevronRight, CalendarClock, Plus, Pencil, Trash2,
  Users, UserCircle, Construction, Zap, ClipboardList, Loader2, DoorClosed, CalendarPlus,
} from "lucide-react";
import { scheduleApi, maintenanceApi, engineersApi, usersApi, buildingsApi, doorsApi, inspectionsApi } from "@/services/api";
import { Link } from "react-router-dom";
import { apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { googleCalendarUrl, microsoft365Url, outlookLiveUrl, buildIcs, scheduleItemToEvent } from "@/lib/googleCalendar";
import { toast } from "sonner";

const KIND_META = {
  maintenance:     { label: "Maintenance", icon: Construction,  tint: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  adhoc_emergency: { label: "Emergency",   icon: Zap,           tint: "#EF4444", bg: "bg-red-50",   text: "text-red-700",   dot: "bg-red-500" },
  inspection:     { label: "Inspection",  icon: ClipboardList, tint: "#3B82F6", bg: "bg-blue-50",  text: "text-blue-700",  dot: "bg-blue-500" },
};
const KINDS = ["maintenance", "adhoc_emergency", "inspection"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toInputValue = (iso) => (iso ? iso.slice(0, 16) : "");

// Iter-227 — Zero-setup "Add to calendar" menu for a scheduled item: opens
// Google / Microsoft 365 / Outlook.com pre-filled, or downloads an .ics.
function AddToCalendarMenu({ item }) {
  const ev = scheduleItemToEvent(item, KIND_META[item.kind]?.label);
  const openUrl = (url) => {
    if (!url) { toast.error("This item has no date to add."); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const downloadIcs = () => {
    const ics = buildIcs(ev);
    if (!ics) { toast.error("This item has no date to add."); return; }
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(ev.title || "event").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "event"}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="Add to calendar" data-testid={`day-cal-${item.id}`}>
          <CalendarPlus className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Add to calendar</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => openUrl(googleCalendarUrl(ev))} data-testid={`cal-google-${item.id}`}>Google Calendar</DropdownMenuItem>
        <DropdownMenuItem onClick={() => openUrl(microsoft365Url(ev))} data-testid={`cal-m365-${item.id}`}>Microsoft 365 (Outlook)</DropdownMenuItem>
        <DropdownMenuItem onClick={() => openUrl(outlookLiveUrl(ev))} data-testid={`cal-outlook-${item.id}`}>Outlook.com</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={downloadIcs} data-testid={`cal-ics-${item.id}`}>Download .ics file</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Schedule() {
  const [mode, setMode] = useState("month");
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dayDate, setDayDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [byAssignee, setByAssignee] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffFilter, setStaffFilter] = useState("all");
  const [staff, setStaff] = useState([]);
  const [dayModal, setDayModal] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [dueBuildings, setDueBuildings] = useState([]);
  useEffect(() => {
    inspectionsApi.due().then((r) => setDueBuildings(r.data?.buildings || [])).catch(() => {});
  }, []);

  const rangeStart = useMemo(() => {
    if (mode === "month") return startOfWeek(startOfMonth(monthDate), { weekStartsOn: 1 });
    if (mode === "week") return weekStart;
    return dayDate;
  }, [mode, monthDate, weekStart, dayDate]);
  const rangeEnd = useMemo(() => {
    if (mode === "month") return addDays(startOfWeek(startOfMonth(monthDate), { weekStartsOn: 1 }), 42);
    if (mode === "week") return addDays(weekStart, 7);
    return addDays(dayDate, 1);
  }, [mode, monthDate, weekStart, dayDate]);

  const load = useCallback(async () => {
    try {
      const start = new Date(rangeStart); start.setHours(0, 0, 0, 0);
      const end = new Date(rangeEnd); end.setHours(23, 59, 59, 999);
      const res = await scheduleApi.get(start.toISOString(), end.toISOString());
      setItems(res.data.items || []);
      setByAssignee(res.data.by_assignee || []);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(apiError(e, "Failed to load schedule"));
      setItems([]); setByAssignee([]);
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [eng, users] = await Promise.all([
          engineersApi.list().catch(() => ({ data: [] })),
          usersApi.list().catch(() => ({ data: [] })),
        ]);
        const engineerList = (eng.data || []).map((e) => ({ id: e.id, name: e.name || e.email }));
        const inspectorList = (users.data || [])
          .filter((u) => u.role === "inspector" || u.role === "admin"
            || (u.secondary_roles || []).includes("inspector") || u.is_company_admin)
          .map((u) => ({ id: u.id, name: u.name || u.email }));
        const map = new Map();
        for (const s of [...engineerList, ...inspectorList]) if (!map.has(s.id)) map.set(s.id, s);
        setStaff(Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } catch { /* non-fatal */ }
    })();
  }, []);

  const filteredItems = useMemo(
    () => (staffFilter === "all" ? items : items.filter((it) => it.assignee_id === staffFilter)),
    [items, staffFilter],
  );
  const filteredByAssignee = useMemo(
    () => (staffFilter === "all" ? byAssignee : byAssignee.filter((a) => a.assignee_id === staffFilter)),
    [byAssignee, staffFilter],
  );

  const monthGridDays = useMemo(() => {
    const first = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 1 });
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
  }, [monthDate]);

  const itemsByDay = useMemo(() => {
    const map = new Map();
    for (const it of filteredItems) {
      if (!it.scheduled_for) continue;
      try {
        const key = format(parseISO(it.scheduled_for), "yyyy-MM-dd");
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(it);
      } catch { /* skip */ }
    }
    return map;
  }, [filteredItems]);

  const goPrev = () => {
    if (mode === "month") setMonthDate((m) => addMonths(m, -1));
    else if (mode === "week") setWeekStart((w) => addDays(w, -7));
    else setDayDate((d) => addDays(d, -1));
  };
  const goNext = () => {
    if (mode === "month") setMonthDate((m) => addMonths(m, 1));
    else if (mode === "week") setWeekStart((w) => addDays(w, 7));
    else setDayDate((d) => addDays(d, 1));
  };
  const goToday = () => {
    if (mode === "month") setMonthDate(startOfMonth(new Date()));
    else if (mode === "week") setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    else setDayDate(new Date());
  };

  const subtitle = mode === "month" ? format(monthDate, "MMMM yyyy")
    : mode === "week" ? `Week of ${format(weekStart, "d MMM yyyy")}`
    : format(dayDate, "EEEE, d MMM yyyy");

  return (
    <div>
      <PageHeader
        title="Schedule"
        subtitle="Plan maintenance, emergency jobs and inspections across your team"
        testId="schedule-header"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg bg-slate-100 p-1">
              {["month", "week", "day"].map((m) => (
                <button
                  key={m}
                  data-testid={`schedule-mode-${m}`}
                  onClick={() => setMode(m)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold capitalize transition-colors ${mode === m ? "bg-primary text-white" : "text-slate-600 hover:text-slate-900"}`}
                >{m}</button>
              ))}
            </div>
            <Button onClick={() => setCreateOpen(true)} data-testid="schedule-new-btn"><Plus className="mr-1.5 h-4 w-4" /> New</Button>
          </div>
        }
      />

      {dueBuildings.length > 0 && (
        <Card className="mb-4 border-amber-200 bg-amber-50/60 p-4" data-testid="due-inspections-panel">
          <div className="mb-2 flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-amber-900">Due &amp; overdue inspections</h3>
          </div>
          <div className="space-y-2">
            {dueBuildings.map((b) => {
              const n = b.door_count;
              let earliest = "";
              if (b.earliest_due) { try { earliest = format(parseISO(b.earliest_due), "d MMM yyyy"); } catch { earliest = ""; } }
              return (
                <div key={b.building_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-100 bg-white px-3 py-2" data-testid={`due-building-${b.building_id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">{b.building_name}</p>
                    <p className="text-xs text-slate-500">
                      {n} door{n === 1 ? "" : "s"} due{b.overdue_count ? ` · ${b.overdue_count} overdue` : ""}{earliest ? ` · earliest ${earliest}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {b.overdue_count > 0 && <Badge variant="outline" className="border-red-200 bg-red-100 text-red-700">{b.overdue_count} overdue</Badge>}
                    <Link to={`/buildings/${b.building_id}`}><Button size="sm" variant="outline" data-testid={`due-view-${b.building_id}`}>View</Button></Link>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Staff filter */}
      <div className="mb-3 flex items-center gap-2 overflow-x-auto pb-1" data-testid="schedule-staff-filter">
        <FilterChip active={staffFilter === "all"} onClick={() => setStaffFilter("all")} testId="staff-filter-all">
          <Users className="h-3.5 w-3.5" /> All staff
        </FilterChip>
        {staff.map((s) => (
          <FilterChip key={s.id} active={staffFilter === s.id} onClick={() => setStaffFilter(s.id)} testId={`staff-filter-${s.id}`}>
            <UserCircle className="h-3.5 w-3.5" /> {s.name}
          </FilterChip>
        ))}
      </div>

      {/* Nav row */}
      <div className="mb-4 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={goPrev} data-testid="schedule-prev">
          <ChevronLeft className="mr-1 h-4 w-4" /> {mode === "month" ? "Prev month" : mode === "week" ? "Prev week" : "Yesterday"}
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">{subtitle}</span>
          <Button variant="ghost" size="sm" onClick={goToday} data-testid="schedule-today"><CalendarClock className="mr-1 h-4 w-4" /> Today</Button>
        </div>
        <Button variant="outline" size="sm" onClick={goNext} data-testid="schedule-next">
          {mode === "month" ? "Next month" : mode === "week" ? "Next week" : "Tomorrow"} <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>

      {loading ? (
        <LoadingState label="Loading schedule…" />
      ) : mode === "month" ? (
        <MonthGrid days={monthGridDays} monthDate={monthDate} itemsByDay={itemsByDay} onDayClick={setDayModal} />
      ) : mode === "week" ? (
        <WeekView weekStart={weekStart} filteredItems={filteredItems} />
      ) : (
        <DayView dayDate={dayDate} rows={filteredByAssignee} />
      )}

      <DayDialog
        date={dayModal}
        items={dayModal ? (itemsByDay.get(format(dayModal, "yyyy-MM-dd")) || []) : []}
        staff={staff}
        onClose={() => setDayModal(null)}
        onChanged={load}
        onAdd={(d) => { setDayModal(null); setCreateOpen(d); }}
      />

      <CreateDialog
        open={!!createOpen}
        presetDate={createOpen instanceof Date ? createOpen : null}
        staff={staff}
        onOpenChange={(v) => setCreateOpen(v ? createOpen : false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
    </div>
  );
}

function FilterChip({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
    >{children}</button>
  );
}

function inspectionDoorLabel(it) {
  if (it.kind !== "inspection") return null;
  const n = it.door_count || 0;
  return n > 0 ? `${n} door${n === 1 ? "" : "s"} booked` : "Whole building";
}

function KindChip({ it }) {
  const meta = KIND_META[it.kind] || KIND_META.maintenance;
  const urgent = it.priority === "high" || it.priority === "urgent";
  const doorLabel = inspectionDoorLabel(it);
  return (
    <div className={`rounded-lg border border-slate-200 bg-white p-2 ${it.kind ? "" : ""}`} style={{ borderLeft: `4px solid ${meta.tint}` }}>
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
        <span className="flex-1 text-[11px] font-bold text-slate-500">{it.scheduled_for ? format(parseISO(it.scheduled_for), "HH:mm") : "—"}</span>
        {urgent && <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-black text-white">!</span>}
      </div>
      <p className="mt-0.5 line-clamp-2 text-[13px] font-semibold text-slate-800">{it.title || meta.label}</p>
      {(it.building_name || it.location_address) && (
        <p className="truncate text-[11px] text-slate-500">
          {it.building_name || it.location_address}{it.door_reference ? ` · ${it.door_reference}` : ""}
        </p>
      )}
      {doorLabel && (
        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
          <DoorClosed className="h-3 w-3" /> {doorLabel}
        </span>
      )}
    </div>
  );
}

function MonthGrid({ days, monthDate, itemsByDay, onDayClick }) {
  return (
    <div data-testid="schedule-month">
      <div className="mb-1 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((d) => <div key={d} className="text-center text-xs font-bold text-slate-500">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const dayItems = itemsByDay.get(key) || [];
          const inMonth = d.getMonth() === monthDate.getMonth();
          const isToday = isSameDay(d, new Date());
          return (
            <button
              key={key}
              data-testid={`month-cell-${key}`}
              onClick={() => onDayClick(d)}
              className={`min-h-[90px] rounded-lg border p-1.5 text-left transition-shadow hover:shadow-md ${inMonth ? "bg-white" : "bg-slate-50 opacity-60"} ${isToday ? "border-2 border-primary" : "border-slate-200"}`}
            >
              <span className={`text-xs font-bold ${isToday ? "text-primary" : "text-slate-700"}`}>{format(d, "d")}</span>
              <div className="mt-1 space-y-0.5">
                {KINDS.map((k) => {
                  const c = dayItems.filter((it) => it.kind === k).length;
                  if (!c) return null;
                  return (
                    <div key={k} className="flex items-center gap-1">
                      <span className={`h-2 w-2 rounded-full ${KIND_META[k].dot}`} />
                      <span className="text-[10px] font-bold text-slate-600">{c}</span>
                    </div>
                  );
                })}
              </div>
              {dayItems.length > 0 && <span className="mt-1 block text-[9px] text-slate-400">{dayItems.length} {dayItems.length === 1 ? "item" : "items"}</span>}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-4">
        {KINDS.map((k) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${KIND_META[k].dot}`} />
            <span className="text-xs font-medium text-slate-500">{KIND_META[k].label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeekView({ weekStart, filteredItems }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7" data-testid="schedule-week">
      {days.map((date) => {
        const dayItems = filteredItems.filter((it) => {
          if (!it.scheduled_for) return false;
          try { return isSameDay(parseISO(it.scheduled_for), date); } catch { return false; }
        });
        const isToday = isSameDay(date, new Date());
        return (
          <div key={date.toISOString()} className="min-w-0">
            <div className={`mb-2 text-center ${isToday ? "text-primary" : "text-slate-700"}`}>
              <p className="text-xs font-bold uppercase">{format(date, "EEE")}</p>
              <p className="text-sm font-semibold">{format(date, "d MMM")}</p>
            </div>
            <div className="space-y-2">
              {dayItems.length === 0
                ? <p className="text-center text-xs text-slate-300">—</p>
                : dayItems.map((it) => <KindChip key={it.id} it={it} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ dayDate, rows }) {
  const withItems = rows
    .map((row) => ({
      ...row,
      items: row.items.filter((it) => {
        try { return isSameDay(parseISO(it.scheduled_for), dayDate); } catch { return false; }
      }),
    }))
    .filter((r) => r.items.length > 0);

  if (withItems.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center py-16 text-center" data-testid="schedule-day-empty">
        <CalendarDays className="h-12 w-12 text-slate-300" />
        <p className="mt-3 text-sm text-slate-500">Nothing scheduled for this day.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="schedule-day">
      {withItems.map((row) => (
        <Card key={row.assignee_id} className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <UserCircle className="h-5 w-5 text-slate-500" />
            <span className="flex-1 font-semibold text-slate-800">{row.assignee_name || "Unassigned"}</span>
            <Badge variant="outline">{row.items.length}</Badge>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {row.items.map((it) => <KindChip key={it.id} it={it} />)}
          </div>
        </Card>
      ))}
    </div>
  );
}

function DayDialog({ date, items, staff, onClose, onChanged, onAdd }) {
  const [editing, setEditing] = useState(null);
  const [engineerId, setEngineerId] = useState("");
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!date) setEditing(null); }, [date]);

  const startEdit = (it) => {
    setEditing(it);
    setEngineerId(it.assignee_id || "");
    setWhen(toInputValue(it.scheduled_for));
    setNotes(it.title || "");
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const iso = when ? new Date(when).toISOString() : undefined;
      if (editing.kind === "inspection") {
        await scheduleApi.updateInspection(editing.id, { scheduled_for: iso, inspector_id: engineerId || undefined, notes: notes || undefined });
      } else {
        await maintenanceApi.update(editing.id, { scheduled_for: iso, engineer_id: engineerId || undefined, notes: notes || undefined });
      }
      toast.success("Schedule updated");
      setEditing(null);
      onChanged();
    } catch (e) { toast.error(apiError(e, "Could not save")); }
    finally { setSaving(false); }
  };

  const doDelete = async (it) => {
    if (!window.confirm(`Remove "${it.title || KIND_META[it.kind].label}" from the schedule?`)) return;
    try {
      if (it.kind === "inspection") await scheduleApi.cancelInspection(it.id);
      else await maintenanceApi.remove(it.id);
      toast.success("Removed from schedule");
      onChanged();
    } catch (e) { toast.error(apiError(e, "Delete failed")); }
  };

  return (
    <Dialog open={!!date} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="schedule-day-dialog">
        <DialogHeader>
          <DialogTitle>{date ? format(date, "EEEE, d MMMM yyyy") : ""}</DialogTitle>
          <DialogDescription>{items.length} scheduled item{items.length === 1 ? "" : "s"}</DialogDescription>
        </DialogHeader>

        {editing ? (
          <div className="space-y-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Edit · {KIND_META[editing.kind].label}</p>
            <p className="font-semibold text-slate-800">{editing.title}</p>
            <div className="space-y-1.5">
              <Label>Reassign to</Label>
              <div className="flex flex-wrap gap-1.5">
                {staff.map((s) => (
                  <button key={s.id} onClick={() => setEngineerId(s.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${engineerId === s.id ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600"}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Date &amp; time</Label>
              <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="day-edit-when" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes / title</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="flex-1" onClick={saveEdit} disabled={saving} data-testid="day-edit-save">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="py-6 text-center text-sm italic text-slate-500">Nothing scheduled. Add a job or inspection below.</p>
            ) : items.map((it) => {
              const meta = KIND_META[it.kind];
              return (
                <div key={it.id} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3" style={{ borderLeft: `4px solid ${meta.tint}` }} data-testid={`day-item-${it.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-black uppercase tracking-wide ${meta.text}`}>{meta.label}</span>
                      <span className="text-[11px] font-bold text-slate-700">{it.scheduled_for ? format(parseISO(it.scheduled_for), "HH:mm") : "—"}</span>
                    </div>
                    <p className="truncate text-sm font-semibold text-slate-800">{it.title || meta.label}</p>
                    <p className="truncate text-xs text-slate-500">{it.assignee_name || "Unassigned"}{it.building_name ? ` · ${it.building_name}` : ""}</p>
                    {inspectionDoorLabel(it) && (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700" data-testid={`day-item-doors-${it.id}`}>
                        <DoorClosed className="h-3 w-3" /> {inspectionDoorLabel(it)}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    <AddToCalendarMenu item={it} />
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => startEdit(it)} data-testid={`day-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => doDelete(it)} data-testid={`day-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
            <Button className="w-full" onClick={() => onAdd(date)} data-testid="day-add">
              <Plus className="mr-1.5 h-4 w-4" /> Add job / inspection on this day
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, presetDate, staff, onOpenChange, onCreated }) {
  const [kind, setKind] = useState("emergency");
  const [saving, setSaving] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  // emergency
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [engineers, setEngineers] = useState([]);
  const [engineerId, setEngineerId] = useState("");
  const [priority, setPriority] = useState("high");
  const [address, setAddress] = useState("");
  const [materials, setMaterials] = useState("");
  // inspection
  const [buildings, setBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState("");
  const [inspectors, setInspectors] = useState([]);
  const [inspectorId, setInspectorId] = useState("");
  const [notes, setNotes] = useState("");
  // inspection building source: pick an existing building or create one on the fly
  const [bldMode, setBldMode] = useState("existing");
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newPostcode, setNewPostcode] = useState("");
  const [newInstructions, setNewInstructions] = useState("");
  // inspection door selection
  const [doors, setDoors] = useState([]);
  const [doorsLoading, setDoorsLoading] = useState(false);
  const [selectedDoors, setSelectedDoors] = useState([]);

  useEffect(() => {
    if (!open) return;
    const base = presetDate ? new Date(presetDate) : (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d; })();
    base.setHours(9, 0, 0, 0);
    setScheduledFor(base.toISOString().slice(0, 16));
    setKind("emergency"); setTitle(""); setDescription(""); setEngineerId(""); setPriority("high");
    setAddress(""); setMaterials(""); setBuildingId(""); setInspectorId(""); setNotes("");
    setBldMode("existing"); setNewName(""); setNewAddress(""); setNewPostcode(""); setNewInstructions("");
    setDoors([]); setSelectedDoors([]);
    (async () => {
      try {
        const [eng, bld, users] = await Promise.all([
          engineersApi.list().catch(() => ({ data: [] })),
          buildingsApi.getAll().catch(() => ({ data: [] })),
          usersApi.list().catch(() => ({ data: [] })),
        ]);
        setEngineers(eng.data || []);
        setBuildings(bld.data || []);
        setInspectors((users.data || []).filter((u) => u.role === "inspector" || u.role === "admin"
          || (u.secondary_roles || []).includes("inspector") || u.is_company_admin));
      } catch { /* non-fatal */ }
    })();
  }, [open, presetDate]);

  // Load the building's doors when the inspection building changes.
  useEffect(() => {
    if (!open || kind !== "inspection" || !buildingId) { setDoors([]); setSelectedDoors([]); return; }
    let cancelled = false;
    setDoorsLoading(true);
    doorsApi.getAll(buildingId)
      .then(({ data }) => { if (!cancelled) { setDoors(Array.isArray(data) ? data : []); setSelectedDoors([]); } })
      .catch(() => { if (!cancelled) setDoors([]); })
      .finally(() => { if (!cancelled) setDoorsLoading(false); });
    return () => { cancelled = true; };
  }, [open, kind, buildingId]);

  const toggleDoor = (doorId) =>
    setSelectedDoors((prev) => (prev.includes(doorId) ? prev.filter((x) => x !== doorId) : [...prev, doorId]));
  const selectMissed = () =>
    setSelectedDoors(doors.filter((d) => ["missed", "no_access"].includes(String(d.last_inspection_status || "").toLowerCase())).map((d) => d.id));
  const missedCount = doors.filter((d) => ["missed", "no_access"].includes(String(d.last_inspection_status || "").toLowerCase())).length;

  const submit = async () => {
    if (!scheduledFor) { toast.error("Pick a date & time."); return; }
    setSaving(true);
    try {
      if (kind === "emergency") {
        if (!title.trim()) throw new Error("Title is required");
        if (!engineerId) throw new Error("Assign to a staff member");
        await scheduleApi.createEmergency({
          title: title.trim(),
          description: description.trim() || undefined,
          engineer_id: engineerId,
          scheduled_for: new Date(scheduledFor).toISOString(),
          priority,
          location_address: address.trim() || undefined,
          materials_estimate: materials ? Number(materials) : undefined,
        });
        toast.success("Emergency job assigned");
      } else {
        if (!inspectorId) throw new Error("Pick an inspector");
        let targetBuildingId = buildingId;
        let inspNotes = notes.trim();
        if (bldMode === "new") {
          if (!newName.trim()) throw new Error("Enter the building name");
          if (!newAddress.trim()) throw new Error("Enter the building address");
          const created = await buildingsApi.create({
            name: newName.trim(),
            address: newAddress.trim(),
            postcode: newPostcode.trim().toUpperCase() || undefined,
            access_codes: newInstructions.trim() || undefined,
          });
          targetBuildingId = created.data.id;
          inspNotes = newInstructions.trim();
        } else if (!buildingId) {
          throw new Error("Pick a building");
        }
        await scheduleApi.createInspection({
          building_id: targetBuildingId,
          inspector_id: inspectorId,
          scheduled_for: new Date(scheduledFor).toISOString(),
          notes: inspNotes || undefined,
          door_ids: bldMode === "existing" && selectedDoors.length ? selectedDoors : undefined,
        });
        toast.success(
          bldMode === "new"
            ? `Inspection booked at ${newName.trim()}`
            : selectedDoors.length
              ? `Inspection booked for ${selectedDoors.length} door${selectedDoors.length === 1 ? "" : "s"}`
              : "Inspection booked (whole building)",
        );
      }
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || apiError(e, "Could not save")); }
    finally { setSaving(false); }
  };

  const staffOptions = engineers.map((e) => ({ id: e.id, label: e.name || e.email }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-md" data-testid="schedule-create-dialog">
        <DialogHeader>
          <DialogTitle>New scheduled item</DialogTitle>
          <DialogDescription>Assign an emergency job or book an inspection.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          {[["emergency", "Emergency job", Zap], ["inspection", "Inspection", ClipboardList]].map(([k, lbl, Icon]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`schedule-kind-${k}`}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-2.5 text-sm font-semibold ${kind === k ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600"}`}>
              <Icon className="h-4 w-4" /> {lbl}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {kind === "emergency" ? (
            <>
              <Field label="Title *"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Emergency lock replacement" data-testid="emergency-title" /></Field>
              <Field label="Description"><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What needs to happen" /></Field>
              <Field label="Assign to *"><PickChips options={staffOptions} value={engineerId} onChange={setEngineerId} empty="No engineers in your company" testIdPrefix="emergency-engineer" /></Field>
              <Field label="Scheduled for *"><Input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} data-testid="schedule-when" /></Field>
              <Field label="Priority">
                <div className="flex gap-2">
                  {["normal", "high", "urgent"].map((p) => (
                    <button key={p} onClick={() => setPriority(p)}
                      className={`flex-1 rounded-lg border py-2 text-xs font-bold uppercase ${priority === p ? "border-transparent text-white" : "border-slate-200 bg-white text-slate-600"}`}
                      style={priority === p ? { backgroundColor: p === "urgent" ? "#EF4444" : p === "high" ? "#F59E0B" : "#10B981" } : {}}>
                      {p}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Location / address"><Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Optional — no building record needed" /></Field>
              <Field label="Materials estimate £"><Input value={materials} onChange={(e) => setMaterials(e.target.value)} placeholder="0.00" inputMode="decimal" /></Field>
            </>
          ) : (
            <>
              <Field label="Building *">
                <div className="mb-2 flex gap-2">
                  {[["existing", "Existing building"], ["new", "New building"]].map(([m, lbl]) => (
                    <button key={m} type="button" onClick={() => setBldMode(m)} data-testid={`insp-bldmode-${m}`}
                      className={`flex-1 rounded-lg border py-2 text-xs font-semibold ${bldMode === m ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {bldMode === "existing" ? (
                  <PickChips options={buildings.map((b) => ({ id: b.id, label: b.name }))} value={buildingId} onChange={setBuildingId} empty="No buildings — add one first" testIdPrefix="insp-building" />
                ) : (
                  <div className="space-y-2">
                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Building name *" data-testid="insp-new-name" />
                    <Input value={newAddress} onChange={(e) => setNewAddress(e.target.value)} placeholder="Address *" data-testid="insp-new-address" />
                    <Input value={newPostcode} onChange={(e) => setNewPostcode(e.target.value)} placeholder="Postcode (optional)" data-testid="insp-new-postcode" />
                    <p className="text-[11px] text-slate-400">A new building record is created so the inspector can add doors and generate reports on site.</p>
                  </div>
                )}
              </Field>
              <Field label="Inspector *"><PickChips options={inspectors.map((u) => ({ id: u.id, label: u.name || u.email }))} value={inspectorId} onChange={setInspectorId} empty="No inspectors on your team" testIdPrefix="insp-user" /></Field>
              {bldMode === "existing" && buildingId && (
                <div className="space-y-1.5" data-testid="insp-door-picker">
                  <div className="flex items-center justify-between">
                    <Label>Doors {selectedDoors.length > 0 ? `(${selectedDoors.length} selected)` : "(all)"}</Label>
                    <div className="flex gap-1.5">
                      {missedCount > 0 && (
                        <button type="button" onClick={selectMissed} data-testid="insp-select-missed"
                          className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                          Missed / no access ({missedCount})
                        </button>
                      )}
                      {selectedDoors.length > 0 && (
                        <button type="button" onClick={() => setSelectedDoors([])} data-testid="insp-select-none"
                          className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-500">Clear</button>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400">Leave all unselected to inspect the whole building, or pick specific doors (e.g. the ones missed or with no access).</p>
                  {doorsLoading ? (
                    <div className="flex items-center gap-2 py-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading doors…</div>
                  ) : doors.length === 0 ? (
                    <p className="py-2 text-xs italic text-slate-500">No doors on this building yet.</p>
                  ) : (
                    <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                      {doors.map((d) => {
                        const sel = selectedDoors.includes(d.id);
                        const outcome = String(d.last_inspection_status || "").toLowerCase();
                        const flag = outcome === "no_access" ? "No access" : outcome === "missed" ? "Missed" : null;
                        return (
                          <button key={d.id} type="button" onClick={() => toggleDoor(d.id)} data-testid={`insp-door-${d.id}`}
                            className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm ${sel ? "border-primary bg-primary/5" : "border-transparent hover:bg-slate-50"}`}>
                            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${sel ? "border-primary bg-primary text-white" : "border-slate-300"}`}>
                              {sel ? "✓" : ""}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{d.door_reference || "Door"}<span className="ml-1 font-normal text-slate-400">{d.location}</span></span>
                            {flag && <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${outcome === "no_access" ? "bg-amber-100 text-amber-700" : "bg-orange-100 text-orange-700"}`}>{flag}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <Field label="Scheduled for *"><Input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} data-testid="schedule-when" /></Field>
              {bldMode === "new" ? (
                <Field label="Access / site instructions"><Textarea rows={2} value={newInstructions} onChange={(e) => setNewInstructions(e.target.value)} placeholder="Access code, key safe, parking, site contact, priority doors…" data-testid="insp-new-instructions" /></Field>
              ) : (
                <Field label="Notes"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Access code, parking notes, priority doors…" /></Field>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="schedule-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {kind === "emergency" ? "Assign emergency job" : "Book inspection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}

function PickChips({ options, value, onChange, empty, testIdPrefix }) {
  if (!options.length) return <p className="text-xs italic text-slate-500">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o.id} onClick={() => onChange(o.id)} data-testid={`${testIdPrefix}-${o.id}`}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${value === o.id ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-600"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
